import { AlertCircle, Building2, Check } from "lucide-react";
import { useState } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";

export interface AcceptInvitationCardProps {
  invitationId: string;
  /** Called after the invitation is accepted and the tenant activated. */
  onAccepted?: (organizationId: string) => void;
}

/**
 * Accepts a tenant invitation from an emailed link. Requires the
 * viewer to be signed in with the invited email — the server enforces
 * the match. On success the new tenant is made active.
 */
export const AcceptInvitationCard = ({
  invitationId,
  onAccepted,
}: AcceptInvitationCardProps) => {
  const client = useUmClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  const accept = async () => {
    setLoading(true);
    setError(null);
    const res = await client.acceptInvitation({ invitationId });
    if (!res.ok) {
      setLoading(false);
      setError(
        errorMessage(
          res.error,
          "This invitation is invalid, expired, or was sent to a different email address."
        )
      );
      return;
    }
    // Make the new tenant active right away so the user lands in it.
    await client.setActiveTenant({
      organizationId: res.data.organizationId,
    });
    setLoading(false);
    setAccepted(true);
    onAccepted?.(res.data.organizationId);
  };

  if (accepted) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Check className="size-5 text-green-600" />
            You're in
          </CardTitle>
          <CardDescription>
            The invitation was accepted and the tenant is now active.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1.5">
        <CardTitle className="flex items-center gap-2 text-2xl">
          <Building2 className="size-5 text-muted-foreground" />
          Tenant invitation
        </CardTitle>
        <CardDescription>
          You've been invited to join a tenant. Accepting adds this account as
          a member.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <Button onClick={accept} loading={loading} className="w-full">
          Accept invitation
        </Button>
      </CardContent>
    </Card>
  );
};
